import { useContext, useEffect, useState, useMemo } from "react"
import { AppContext } from "../App"
import { getInlineLoader } from "../Helpers";
import {
    STR_CONTRIBUTIONS,
    STR_NO_RECORD,
    STR_SEARCH_HERE,
    STR_CONTRIBUTION_TOTAL,
    STR_LOANS
} from "../Strings";
import { MdSearch, MdPeople, MdPerson, MdCalendarToday, MdAccountBalanceWallet, MdTrendingUp, MdSwipe, MdChevronRight } from 'react-icons/md';

export default function View(props) {
    const appContext = useContext(AppContext);
    const [ready, setReady] = useState(false);
    const [filter, setFilter] = useState("");
    const [activeTab, setActiveTab] = useState("all");
    const [showScrollPrompt, setShowScrollPrompt] = useState(false);

    async function init() {
        setReady(false);
        await Promise.all([appContext.getContributions()])
        setReady(true);
    }

    useEffect(() => { init(); }, []);

    const filteredList = useMemo(() => {
        const list = appContext.contributions || [];
        const filtered = list.filter(item => {
            const isMine = String(item.fullname).toLowerCase() === String(appContext.currentUserData?.fullname).toLowerCase();
            const matchesSearch = String(item.fullname).toLowerCase().includes(filter.toLowerCase());
            return activeTab === "mine" ? isMine : matchesSearch;
        });
        return filtered.sort((a, b) => new Date(b.dateCreated) - new Date(a.dateCreated));
    }, [appContext.contributions, appContext.currentUserData, filter, activeTab]);

    // Grouping logic for the Date-Based approach
    const groupedContributions = useMemo(() => {
        const groups = {};
        filteredList.forEach(item => {
            const date = new Date(item.dateCreated);
            const monthYear = date.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
            if (!groups[monthYear]) groups[monthYear] = [];
            groups[monthYear].push(item);
        });
        return groups;
    }, [filteredList]);

    const grandTotal = useMemo(() => {
        return filteredList.reduce((sum, item) => sum + parseFloat(item.amount || 0), 0);
    }, [filteredList]);

    if (!ready) {
        return <div className="d-flex vh-100 align-items-center justify-content-center">{getInlineLoader()}</div>;
    }

    return (
        <div className="mNoScrollBar" style={{ 
            width: "100%", height: "100%", overflowY: "auto", 
            backgroundColor: "#f8fafc", paddingBottom: "100px",
            position: "relative"
        }}>
            <div className="container py-4">
                
                {/* Summary Card remains as requested */}
                <div className="card border-0 shadow-sm mb-4" style={{ borderRadius: '20px', background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)', color: 'white' }}>
                    <div className="card-body p-4">
                        <div className="d-flex justify-content-between align-items-center">
                            <div>
                                <h5 className="text-primary font-bold mb-1">{STR_CONTRIBUTION_TOTAL[appContext.language]}</h5>
                                <h1 className="font-bold mb-0">{appContext.currency} {grandTotal.toLocaleString()}</h1>
                                <div className="w-50 mt-2 small bg-info bg-opacity-25 text-info d-flex align-items-center justify-content-center px-3 py-1 rounded-pill">
                                    <MdTrendingUp className="me-1" /> <span className="font-bold">{filteredList.length}</span>
                                </div>
                            </div>
                            <MdAccountBalanceWallet size={40} className="opacity-25" />
                        </div>
                    </div>
                </div>

                {/* Filters & Search remain as requested */}
                <div className="row g-3 mb-4">
                    <div className="col-12 col-lg-5">
                        <div className="bg-white p-1 d-flex shadow-sm rounded-pill">
                            <button className={`btn flex-fill border-0 rounded-pill py-2 ${activeTab === 'all' ? 'bg-primary text-white shadow' : 'text-muted'}`} onClick={() => setActiveTab('all')} style={{ fontSize: '14px', fontWeight: '600' }}><MdPeople className="me-1" /> All</button>
                            <button className={`btn flex-fill border-0 rounded-pill py-2 ${activeTab === 'mine' ? 'bg-primary text-white shadow' : 'text-muted'}`} onClick={() => { setActiveTab('mine'); setFilter(""); }} style={{ fontSize: '14px', fontWeight: '600' }}><MdPerson className="me-1" /> Mine</button>
                        </div>
                    </div>
                    {activeTab === "all" && (
                        <div className="col-12 col-lg-7">
                            <div className="input-group bg-white shadow-sm rounded-pill px-3 border-0 d-flex align-items-center">
                                <MdSearch size={22} className="text-muted me-2" />
                                <input className="form-control border-0 bg-transparent ps-2" onChange={(e) => setFilter(e.target.value)} value={filter} placeholder={STR_SEARCH_HERE[appContext.language]} style={{ height: '45px', boxShadow: 'none' }} />
                            </div>
                        </div>
                    )}
                </div>

                {/* Date-Based Clean Ledger Interface */}
                <div className="ledger-container">
                    {Object.keys(groupedContributions).length > 0 ? (
                        Object.entries(groupedContributions).map(([monthYear, items]) => (
                            <div key={monthYear} className="mb-4">
                                <div className="d-flex align-items-center mb-2 px-2">
                                    <div className="bg-primary rounded-pill" style={{ width: '4px', height: '16px' }}></div>
                                    <h6 className="ms-2 mb-0 fw-bold text-dark text-uppercase small" style={{ letterSpacing: '1px' }}>
                                        {monthYear}
                                    </h6>
                                    <div className="ms-auto badge bg-light text-muted rounded-pill border fw-normal">
                                        {items.length} records
                                    </div>
                                </div>

                                <div className="card border-0 shadow-sm overflow-hidden" style={{ borderRadius: '18px' }}>
                                    <div className="list-group list-group-flush">
                                        {items.map((item, i) => (
                                            <div key={item.id || i} className="list-group-item list-group-item-action border-0 px-3 py-3 d-flex align-items-center" style={{ transition: '0.2s' }}>
                                                {/* Date Circle */}
                                                <div className="text-center me-3" style={{ minWidth: '45px' }}>
                                                    <span className="d-block fw-bold text-dark mb-0" style={{ fontSize: '1.1rem', lineHeight: '1' }}>
                                                        {new Date(item.dateCreated).getDate()}
                                                    </span>
                                                    <span className="text-muted text-uppercase" style={{ fontSize: '10px', fontWeight: '700' }}>
                                                        {new Date(item.dateCreated).toLocaleDateString('en-GB', { weekday: 'short' })}
                                                    </span>
                                                </div>

                                                {/* Avatar & Name */}
                                                <div className="d-flex align-items-center flex-grow-1 overflow-hidden">
                                                    <div className="me-2 rounded-circle bg-dark text-white d-flex align-items-center justify-content-center fw-bold" 
                                                         style={{ minWidth: '32px', width: '32px', height: '32px', fontSize: '11px' }}>
                                                        {item.fullname?.charAt(0).toUpperCase()}
                                                    </div>
                                                    <div className="text-truncate">
                                                        <div className="fw-semibold text-dark text-truncate" style={{ fontSize: '14px' }}>{item.fullname}</div>
                                                    </div>
                                                </div>

                                                {/* Amount */}
                                                <div className="text-end ms-2">
                                                    <div className="fw-bold text-primary" style={{ fontSize: '15px' }}>
                                                        {appContext.currency} {parseFloat(item.amount || 0).toLocaleString()}
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        ))
                    ) : (
                        <div className="text-center py-5 bg-white rounded shadow-sm">
                            <h6 className="text-muted fw-light">{STR_NO_RECORD[appContext.language]}</h6>
                        </div>
                    )}
                </div>
            </div>
        </div>
    )
}